// Stage Mail-V2 Composer (2026-07-23): RichTextEditorCore — Mail V2 独立 TipTap 富文本编辑器。
//
// Purpose:
//   为 Mail V2 提供独立的富文本正文编辑器,接入 MailComposerContent 共享层。
//   不依赖 Blog 模块任何代码;不复用 BlogDraftEditor / BlogDraftEditorToolbar。
//
// 设计:
//   - TipTap 2.27.x StarterKit + Underline + Link(沿用 Blog 技术栈)
//   - controlled value:外部传入 html,onChange 输出 {html, text}
//   - 外部 setContent 循环守卫:仅当 incoming 与 editor.getHTML() 不同时才 setContent
//   - setContent 用 emitUpdate=false,避免 emit → onUpdate → onChange 循环
//   - sanitize:不在前端做(后端 utils.sanitize_html 已处理;前端不允许信任用户粘贴 HTML)
//
// 不支持(Mail V2 第一阶段):
//   - Strike / <s> / <del>(后端 sanitizer allowlist 不含,sanitize 后会被剥)
//   - 内联图片(后端 sanitizer 允许 img,但本阶段不启用 UI 按钮)
//   - Block kind dropdown / heading(第一版仅 paragraph + list)
//
// 非目的:
//   - 不持有 to/cc/subject/draftId 状态(由 useMailComposer 管理)
//   - 不调 API
//   - 不做 autosave(由 useMailComposer 处理)

import { useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";

import { RichTextEditorCoreToolbar } from "./RichTextEditorCoreToolbar";
import { plainTextToHtml, htmlToPlainText } from "./plainTextHtml";

export interface RichTextEditorCoreProps {
  /** 当前 HTML 值(controlled)。父组件确保是 trusted 来源(后端已 sanitize)。 */
  value: string;
  /** 编辑器内容变化时回调。同时给 html(用于持久化)和 text(用于 SMTP fallback / 纯文本兜底)。 */
  onChange: (next: { html: string; text: string }) => void;
  placeholder?: string;
  /** 整个编辑器禁用,用于 send 飞行中 / read-only 场景。 */
  disabled?: boolean;
  /** 与 disabled 区分:readOnly 只锁编辑,不影响 toolbar 状态可视;disabled 完全禁用 toolbar。 */
  readOnly?: boolean;
  /** 编辑区最小高度。number = px(默认 240px);string = CSS 长度,如 "100%"(flex 撑满剩余空间)。 */
  minHeight?: number | string;
  /** 失焦回调(autosave 触发可在此)。 */
  onBlur?: () => void;
  /** 额外的编辑器容器 class。 */
  className?: string;
  /** test id */
  "data-testid"?: string;
}

// ---- 内部 helpers ----

function cx(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(" ");
}

// 仅支持 Mail V2 第一阶段允许的 TipTap extensions。
// Strike / heading / blockquote 不引入(后端 sanitizer 会剥 <s> 等)。
const SHARED_EXTENSIONS = [
  StarterKit.configure({
    // StarterKit 自带 heading / blockquote / codeBlock 等 — 这里不主动禁,
    // 但 Phase 2 工具栏不暴露这些按钮。如要禁,Phase 后续可加 configure 显式 disable。
    heading: false,
    blockquote: false,
    codeBlock: false,
    horizontalRule: false,
  }),
  Underline,
  Link.configure({
    openOnClick: false,
    autolink: true,
    // Mail V2 仅允许 http(s) / mailto / 相对路径 / 片段,与 sanitizer 一致。
    // TipTap 自带校验:href 必须是合法 URI。
    protocols: ["http", "https", "mailto"],
  }),
] as const;

// ---- 本体 ----

export function RichTextEditorCore({
  value,
  onChange,
  placeholder = "本文を入力",
  disabled = false,
  readOnly = false,
  minHeight = 240,
  onBlur,
  className,
  "data-testid": dataTestId,
}: RichTextEditorCoreProps) {
  // 来自 props 的当前 html — 上一次 emit 之后的"期望值"。
  // 用来判断:外部 value 变化时,editor.getHTML() 是否已与外部一致。
  const lastCommittedHtmlRef = useRef<string>(value ?? "");

  // 切 draft 时:useEditor 的 content 由 useEffect 同步设置,不可变 deps 用 mount 时 snapshot。
  // TipTap 自身支持 deps 触发 reload;此处我们保持 editor 实例稳定,只通过
  // onUpdate 持续更新 ref;外部 value 变化走 useEffect 路径 setContent。

  const editor: Editor | null = useEditor(
    {
      extensions: [...SHARED_EXTENSIONS],
      content: value ?? "",
      editable: !disabled && !readOnly,
      onUpdate: ({ editor: ed }) => {
        const nextHtml = ed.getHTML();
        if (nextHtml === lastCommittedHtmlRef.current) return;
        lastCommittedHtmlRef.current = nextHtml;
        // text 用 editor.getText() 派生(不去正则,走 ProseMirror 文档)
        const nextText = htmlToPlainText(ed.getText());
        onChange({ html: nextHtml, text: nextText });
      },
    },
    // 重建条件:disabled/readOnly 切换才重建 editor;value 不在 deps(我们手动同步)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, readOnly],
  );

  // 外部 value 变化 → 同步到 editor。
  // 守卫规则:
  //   1) 若 editor 内部已与 value 一致 → 跳过
  //   2) 若 value 是空字符串而 editor 也是空(空文档 ProseMirror 表示 "<p></p>")→ 跳过
  //   3) 否则 setContent(value, emitUpdate=false)— 不触发 onUpdate,避免循环
  //
  // 旧 Draft 兼容:
  //   当父组件传入 value 是空字符串但同时传 bodyText(由父组件在 plainTextToHtml 转换后传入),
  //   此时 setContent 会被调用一次,把 plain→html 写进 editor。这是正常路径。
  useEffect(() => {
    if (!editor) return;
    const incoming = value ?? "";
    if (incoming === lastCommittedHtmlRef.current) {
      return;
    }
    // 编辑器当前 HTML 与 incoming 完全一致 → 也跳过
    try {
      if (editor.getHTML() === incoming) {
        lastCommittedHtmlRef.current = incoming;
        return;
      }
    } catch {
      // editor 在 destroy / remount 过程中 getHTML 可能抛;不做对比直接尝试 setContent。
    }
    // emitUpdate=false 避免触发 onUpdate,防止外部 value → setContent → onUpdate
    // → onChange({html, text}) → 父组件 setField → value 又变(同值)→ setContent
    // 这样的循环被 lastCommittedHtmlRef 拦下,但 emitUpdate=false 是第二道保险。
    try {
      editor.commands.setContent(incoming || "", false);
      lastCommittedHtmlRef.current = incoming;
    } catch {
      // setContent 偶尔抛 schema 错误 — 父组件应保证传 sanitize 后的 HTML。
      // 这里静默失败,不让整个 composer 崩。
    }
  }, [value, editor]);

  const handleBlur = useCallback(() => {
    if (onBlur) onBlur();
  }, [onBlur]);

  const editable = !disabled && !readOnly;

  return (
    <div
      className={cx("mv2-rte", className)}
      data-testid={dataTestId ?? "mv2-rte"}
      data-disabled={disabled || undefined}
      data-readonly={readOnly || undefined}
    >
      <RichTextEditorCoreToolbar editor={editor} disabled={disabled} />
      <div
        className={cx("mv2-rte-content", !editable && "mv2-rte-content--readonly")}
        data-placeholder={placeholder}
        style={{ minHeight }}
      >
        <EditorContent
          editor={editor}
          onBlur={handleBlur}
        />
      </div>
    </div>
  );
}

// ---- 工具:供 MailComposerContent 在切 Draft 时决定是否 plainTextToHtml ----

/**
 * 当 draft 的 body_html 为空但 body_text 非空时,父组件调用本函数
 * 把纯文本转换为安全 HTML 作为编辑器初值。
 *
 * 安全规则:
 *   - 纯文本中所有 <,>,&,",' 都 escape
 *   - 连续两个换行 → 段落分隔(每个段落 <p>...</p>)
 *   - 单个换行 → <br />
 *   - 不会执行任何用户 HTML
 *   - 不会插 <script>/<img>/<a> 等危险标签
 */
export { plainTextToHtml };

// ---- 默认导出 ----
export default RichTextEditorCore;
